/**
 * RPDM Wave 2-UI · Phase 2 · Delivery Sub-Navigation.
 *
 * Renders the six W2 delivery capabilities as horizontal sub-nav
 * entries. In Phase 2 only `hierarchy` is functionally enabled and
 * only when the `rpdm_w2_hierarchy_enabled` flag is ON.
 *
 * Every entry carries a stable `data-testid` per Rev.2 § 20:
 *   * `rpdm-delivnav-<key>`          — enabled entry (a `<NavLink>`).
 *   * `rpdm-delivnav-<key>-disabled` — disabled entry (a `<span>` with
 *     `aria-disabled="true"` and a "Feature not enabled" tooltip).
 *
 * Read-only surface: no create / edit / rename / delete controls.
 * Keyboard-accessible: enabled links join the normal tab order;
 * disabled entries expose the same label via `aria-disabled` so
 * assistive tech can announce "unavailable".
 */
import { NavLink, useParams } from "react-router-dom";
import { useRpdmWave2Gates } from "../../../../hooks/useRpdmWave2Gates";

const ITEMS = [
    { key: "overview", label: "Overview" },
    { key: "hierarchy", label: "Hierarchy" },
    { key: "dependencies", label: "Dependencies" },
    { key: "progress", label: "Progress" },
    { key: "effort", label: "Effort" },
    { key: "analytics", label: "Analytics" },
];

export default function DeliverySubNav({ engagementId }) {
    const { id: idFromRoute } = useParams();
    const engId = engagementId || idFromRoute;
    const gates = useRpdmWave2Gates();

    // Phase 2 enables only the hierarchy tab.
    const isEnabled = (key) => key === "hierarchy" && gates.hierarchy === true;

    return (
        <nav
            data-testid="rpdm-delivery-subnav"
            aria-label="Delivery capabilities"
            className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50/40 px-2 py-2"
        >
            {ITEMS.map((item) => {
                if (!isEnabled(item.key)) {
                    return (
                        <span
                            key={item.key}
                            data-testid={`rpdm-delivnav-${item.key}-disabled`}
                            role="link"
                            aria-disabled="true"
                            title={`${item.label} — feature not enabled`}
                            className="cursor-not-allowed rounded-md px-3 py-1.5 text-sm text-slate-400"
                        >
                            {item.label}
                        </span>
                    );
                }
                return (
                    <NavLink
                        key={item.key}
                        data-testid={`rpdm-delivnav-${item.key}`}
                        to={`/projects/${engId}/delivery/${item.key}`}
                        className={({ isActive }) =>
                            `rounded-md px-3 py-1.5 text-sm transition-colors ${
                                isActive
                                    ? "bg-slate-900 font-medium text-white"
                                    : "text-slate-700 hover:bg-slate-100"
                            }`
                        }
                    >
                        {item.label}
                    </NavLink>
                );
            })}
        </nav>
    );
}
